import { parsePhotoErase } from './photo-erase.js';
import { textLength } from './emoji.js';
/**
 * The free-form personalizer: a print zone the shopper fills with layers of
 * their own — text, photos, artwork (cliparts, emoji), drawn shapes.
 *
 * Three ideas hold the whole feature together:
 *
 *  1. **The answer is in millimetres, in the zone's own frame.** A layer's box
 *     is `{x, y, w, h, rotation}` with x/y the CENTRE measured from the zone's
 *     top-left corner, unrotated, in mm. The same answer therefore lands on the
 *     print file (zone at 300 dpi) and on every mockup copy (zone drawn small
 *     on a product photo, possibly tilted or in perspective) through one
 *     function, `layerTransform`, with no re-authoring. Text size is `sizeMm`,
 *     the em height on paper — what "8 mm letters" means to a print shop.
 *
 *  2. **Nothing new is drawn.** `expandDesignAreas` turns a zone into native
 *     `text` / `image` / `vector` / `shape` elements clipped to the zone before
 *     any renderer sees the scene. The widget's live preview, the cart
 *     thumbnail and the production file all come out of the one renderer, and
 *     the vector export keeps text and shapes as outlines.
 *
 *  3. **The template author sets the rules, this module enforces them on both
 *     sides.** `designAreaProblems` is the single validator: the widget runs it
 *     to explain, the API runs it to refuse. Fonts, palettes, allowed
 *     libraries, layer counts, text length, resolution — one list of codes.
 *
 * Fancy Product Designer is the reference for the shopper experience
 * (custom text/image/design adds, bounding-box "clipping" vs "inside",
 * quality ratings by DPI, price per added element); the model here is our
 * own, unit-based and versioned.
 */

import { coverPhotoFrame } from './photo-framing.js';
import { designerLook } from './designer-looks.js';
import type {
  Bounds,
  Color,
  DesignAreaElement,
  ImageCrop,
  ImageEffect,
  ImageElement,
  Point,
  Scene,
  SceneElement,
  ShapeElement,
  TextCurve,
  TextElement,
  Transform,
  VectorElement,
} from './types.js';
import { isShapeKind, shapeDef, type ShapeKind } from './shapes.js';
import { STICKER_LIMITS, validStickerSettings, type StickerSettings } from './sticker.js';
import { designerPatternTiles, parseDesignerPattern, designerVerticalText, validateDesignerQr, designerQrMinMm, type DesignerPattern } from './designer-pattern.js';

export const DESIGN_ANSWER_VERSION = 1 as const;

/** The option type a zone is asked through, and the binding property. */
export const DESIGN_AREA_OPTION_TYPE = 'design_area';
export const DESIGN_AREA_PROPERTY = 'design';

// ── Configuration (authored in Studio, shipped in the definition) ────────────

/** A one-tap text look: fill + outline (+ shadow) chosen together. */
export interface DesignTextStyle {
  id: string;
  /** Shopper-facing name, e.g. "Bela sa crnim okvirom". */
  name: string;
  color: Color;
  /** Outline width as a FRACTION of the font size (0.08 = 8 %). */
  outline?: { color: Color; width: number };
  /** Percent-of-font-size shadow, like `TextShadow.relative`. */
  shadow?: { color: Color; offset: number; angle: number; blur: number };
}

export interface DesignAreaConfig {
  /** Original shop-owned layouts, frozen with the product release. */
  templates?: import('./designer-templates.js').DesignerTemplate[];
  /** Manufacturing capability, explicitly enabled per product zone. */
  sticker?: { allowed: boolean; required?: boolean; defaults?: StickerSettings };
  /**
   * Physical size of the zone. Authoritative: on the print side the element's
   * transform is kept equal to this at the scene's dpi; on a mockup the copy's
   * transform is whatever the owner placed, and `transform.w / sizeMm.w` is
   * the copy's scale. Millimetres, because that is the unit the print shop and
   * the shopper are told ("7.5 × 5 cm").
   */
  sizeMm: { w: number; h: number };
  /** Inner guide; layers may cross it, the designer warns. Default 0. */
  safeMarginMm?: number;
  /**
   * Trim allowance OUTSIDE the zone, in mm — Zazzle's "bleed line".
   *
   * A guide and nothing else: it changes no geometry, no expansion and no
   * validation. The designer draws a dashed line `bleedMm` beyond the zone edge
   * so the shopper knows how far to push a background before the guillotine can
   * expose a white rim. Default 0 (no bleed line).
   */
  bleedMm?: number;
  /**
   * `clip` (default, FPD "clipping"): paint outside the zone is cut off.
   * `inside` (FPD "inside"): every layer must lie wholly inside; the commit
   * refuses otherwise (through `bounds.mode: 'inside'`).
   */
  boundsMode?: 'clip' | 'inside';
  /** What the shopper may add. Everything defaults to allowed. */
  allow?: { text?: boolean; image?: boolean; artwork?: boolean; emoji?: boolean; shape?: boolean; qr?: boolean; background?: boolean; pattern?: boolean };
  /** Default 20. */
  maxLayers?: number;
  text?: {
    /** Fonts on offer; absent/empty = every font of the shop. */
    fontIds?: string[];
    defaultFontId?: string;
    /** Fill colours; absent = `DESIGN_PALETTE`. */
    palette?: Color[];
    /** The "Za štampu" grid behind "Dodatne boje"; absent = `DESIGN_PRINT_SAFE_PALETTE`. */
    printPalette?: Color[];
    /** The "Web" grid behind "Dodatne boje"; absent = `DESIGN_WEB_PALETTE`. */
    webPalette?: Color[];
    /** A free colour picker beyond the palette. Default true. */
    allowCustomColor?: boolean;
    /** Outline colours; absent = palette plus black and white. */
    outlinePalette?: Color[];
    /** One-tap looks; absent = `DESIGN_TEXT_STYLES`. */
    styles?: DesignTextStyle[];
    /** Characters per text layer. Default 60. */
    maxChars?: number;
    /** Lines per text layer. Default 3. */
    maxLines?: number;
    allowCurve?: boolean;
    allowShadow?: boolean;
    /** Starter text of a new layer. Default "Tvoj tekst". */
    defaultText?: string;
    /** Em size bounds in mm. Defaults 2 and the zone's shorter side. */
    minSizeMm?: number;
    maxSizeMm?: number;
  };
  image?: {
    /** Below this the designer warns ("slab kvalitet"). Default 150. */
    minDpi?: number;
    /** Below this the commit refuses. Default 72. */
    blockBelowDpi?: number;
    allowRemoveBackground?: boolean;
    allowFilters?: boolean;
    maxCount?: number;
  };
  artwork?: {
    /** Collections on offer; absent = every built-in pack plus storefront libraries. */
    libraryIds?: string[];
    /** Recolour vector artwork. Default true. */
    allowRecolor?: boolean;
  };
  shape?: {
    /** Absent = every shape. */
    kinds?: ShapeKind[];
    /** Absent = `DESIGN_PALETTE`. */
    palette?: Color[];
  };
  /** Surcharges, in the shop currency; all optional. */
  pricing?: { base?: number; perText?: number; perImage?: number; perArtwork?: number; perShape?: number; max?: number };
  /** Caption of the empty zone in the designer. */
  placeholder?: string;
  /**
   * Zazzle's print location ("Front" / "Front Pocket"): zones sharing `group`
   * are alternatives on one side and the shopper designs one of them. See
   * `print-location.ts`; set through `configurePrintLocation`.
   */
  printLocation?: import('./print-location.js').PrintLocation;
}

// ── The answer (what the shopper made) ───────────────────────────────────────

/** A layer's box in the zone frame: centre from the zone's top-left, mm. */
export interface DesignBox {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Degrees, clockwise. */
  rotation?: number;
}

interface DesignLayerBase {
  name?: string;
  /** Persistent output visibility; distinct from a temporary editor hide. */
  visible?: boolean;
  pattern?: DesignerPattern;
  /** Stable editing group; no effect on paint order or production. */
  groupId?: string;
  /** Unique within the answer; stable while the layer lives. */
  id: string;
  box: DesignBox;
  /** 0–1, absent = opaque. */
  opacity?: number;
  flipX?: boolean;
  flipY?: boolean;
  /** Frozen in the designer (a shopper's own choice); render as usual. */
  locked?: boolean;
  /**
   * Zazzle-style "Selling Options", authored in Studio on a zone's starting
   * composition. A template object is a placeholder the shopper is meant to
   * replace: its `label` names the field ("Vaše ime"), `key` links duplicates
   * (one edit updates every layer with the same key), `editOnCard` puts a
   * quick field on the product card, `mustReplace` blocks checkout until the
   * shopper changed it (`original` remembers the authored text or asset id).
   */
  template?: DesignTemplateField;
  /** The shopper may move, scale and edit it, but never delete it. */
  permanent?: boolean;
  /** `false` = the shopper cannot select or move it; it prints as authored. */
  interactive?: boolean;
}

export interface DesignTemplateField {
  label: string;
  key?: string;
  editOnCard?: boolean;
  mustReplace?: boolean;
  /** The authored text (text layers) or asset id (image/artwork layers). */
  original?: string;
}

export type DesignImageEffect =
  | { kind: 'removeBackground' }
  | { kind: 'grayscale' }
  | { kind: 'sepia' }
  | { kind: 'brightness'; amount: number }
  | { kind: 'contrast'; amount: number }
  /**
   * Zazzle "Remove white from image": near-white pixels become clear — all of
   * them, or with `connected` only the white touching the picture's border
   * ("background only").
   */
  | { kind: 'whiteTransparent'; connected?: true }
  /** One of the designer's photo looks (`DESIGNER_LOOKS`), by id. */
  | { kind: 'look'; id: string };

export interface DesignTextLayer extends DesignLayerBase {
  kind: 'text';
  /** Lines separated by `\n`. */
  text: string;
  orientation?: 'horizontal' | 'vertical';
  fontId: string;
  /** Em height in mm. */
  sizeMm: number;
  color: Color;
  align?: 'left' | 'center' | 'right';
  /**
   * How the box relates to the letters — Zazzle's two kinds of text object.
   *
   * Absent (or `'auto'`) is the box that HUGS its text: the designer measures
   * the line and fits `box` to the ink, so the width is a consequence of what
   * was typed. `'fixed'` is Zazzle's ordinary text box: the width is the
   * shopper's own (the side handles set it), the text WRAPS inside it, and the
   * height follows the number of lines that wrapping produced — see
   * {@link fixedTextBoxHeightMm}. A fixed box and a bend are mutually
   * exclusive: a line that wraps has no single arc to follow, so `boxMode`
   * wins and `bend` is dropped by the normaliser.
   */
  boxMode?: 'auto' | 'fixed';
  /**
   * Where the lines sit inside the box — Zazzle's "Text align → Anchor Top /
   * Anchor Center / Anchor Bottom" (its `palign`). Absent = `'middle'`.
   *
   * It only shows on a box taller than its text, which in practice means a
   * fixed one: an auto box is fitted to the ink, so all three anchors draw the
   * same picture there.
   */
  anchorV?: 'top' | 'middle' | 'bottom';
  /** Multiplier, default 1.15. */
  lineSpacing?: number;
  /** Thousandths of an em, as `TextElement.tracking`. */
  tracking?: number;
  uppercase?: boolean;
  /** Width as a fraction of the font size (see `OUTLINE_WIDTHS`). */
  outline?: { color: Color; width: number };
  /** Percent-of-font-size shadow. */
  shadow?: { color: Color; offset: number; angle: number; blur: number };
  /**
   * Curvature, -100..100: 0 straight, 100 a semicircle arching upward,
   * -100 a semicircle hanging downward. See `bendCurve`.
   */
  bend?: number;
  /**
   * Advance width of the STRAIGHT text at `sizeMm`, in mm, measured by the
   * designer. Needed to turn `bend` into a radius before any glyph is laid
   * out; the print renderer has the same fonts but expansion is pure and
   * synchronous, so the measurement travels with the answer.
   */
  textWidthMm?: number;
  /** The preset this look came from, for the designer's chips only. */
  styleId?: string;
}

export interface DesignImageLayer extends DesignLayerBase {
  erase?: import('./photo-erase.js').PhotoEraseStroke[];
  kind: 'image';
  maskShape?: ShapeKind;
  /** A shopper upload (asset of kind `upload` owned by the session). */
  assetId: string;
  crop?: ImageCrop;
  fit?: 'cover' | 'contain';
  effects?: DesignImageEffect[];
  /** Source pixels as the designer saw them; the server re-reads the asset. */
  sourcePx?: { w: number; h: number };
}

export interface DesignArtworkLayer extends DesignLayerBase {
  kind: 'artwork';
  /** An asset from an allowed library (clipart, emoji, icon). */
  assetId: string;
  libraryId?: string;
  /** True for an SVG; drives which recolouring applies. */
  vector?: boolean;
  /** Vector: colour-for-colour swaps. */
  colorMap?: Array<{ from: string; color: Color }>;
  /** Vector: paint the whole artwork one colour. */
  singleColor?: Color;
  /** Raster: tint through the picture's alpha. */
  colorize?: Color;
}

export interface DesignShapeLayer extends DesignLayerBase {
  kind: 'shape';
  shape: ShapeKind;
  fill?: Color;
  stroke?: { color: Color; widthMm: number };
}

export interface DesignQrLayer extends DesignLayerBase {
  kind: 'qr';
  url: string;
}

export type DesignLayer = DesignTextLayer | DesignImageLayer | DesignArtworkLayer | DesignShapeLayer | DesignQrLayer;

export interface DesignAreaAnswer {
  v: typeof DESIGN_ANSWER_VERSION;
  sticker?: StickerSettings;
  /** Printed colour underneath the composition. Absent means transparent. */
  background?: Color;
  /** Paint order, first at the bottom. */
  layers: DesignLayer[];
}

// ── Defaults ─────────────────────────────────────────────────────────────────

const c = (hex: string): Color => ({ hex });

/** Print-friendly fills: saturated, no near-duplicates, black and white first. */
export const DESIGN_PALETTE: Color[] = [
  '#000000', '#ffffff', '#e63946', '#ff6b81', '#ff8fab', '#f72585', '#9d4edd', '#5a189a',
  '#3a86ff', '#0077b6', '#48cae4', '#2ec4b6', '#06d6a0', '#2a9d8f', '#8ac926', '#ffca3a',
  '#ffb703', '#fb8500', '#f4a261', '#c8553d', '#8d5524', '#6c757d', '#adb5bd', '#ffd6e0',
].map(c);

/**
 * The "Dodatne boje → Za štampu" grid: colours that survive a CMYK press.
 *
 * Six rows of twelve — neutrals, then tint/soft/mid/deep/darkest bands across
 * one hue wheel. Nothing here sits at an RGB extreme, because those are exactly
 * the colours a shopper picks on screen and then does not recognise on paper.
 * `text.printPalette` overrides it per product.
 */
export const DESIGN_PRINT_SAFE_PALETTE: Color[] = [
  '#ffffff', '#f2f2f2', '#e0e0e0', '#c9c9c9', '#b0b0b0', '#949494', '#767676', '#5c5c5c', '#444444', '#2e2e2e', '#1a1a1a', '#000000',
  '#fde2e4', '#fde4cf', '#fdf6bd', '#e2f0cb', '#d4f0e0', '#cfe8ef', '#d7e3fc', '#ded3f2', '#f0d7ef', '#f7d6e0', '#ece0d1', '#e8e8d8',
  '#f6a6a6', '#f7c59f', '#f9e07f', '#c3e08a', '#9fe0c1', '#a3d5e3', '#a9c4f5', '#c0aee8', '#e3aede', '#eeb0c6', '#d7b49b', '#cfcfa8',
  '#d7504b', '#e08a3c', '#e8c03a', '#8bb741', '#34a878', '#2f8fa8', '#3f6fc4', '#7b57b5', '#b4479f', '#cf5480', '#a4744a', '#8f9440',
  '#a5322e', '#b0632a', '#b28f1f', '#5f8a26', '#1d7b55', '#1f6a80', '#2b4d93', '#573b88', '#852f74', '#9c3a5d', '#77522f', '#676b26',
  '#6e211f', '#74411c', '#765f14', '#3e5a18', '#13513a', '#144657', '#1c3363', '#3a285c', '#59204e', '#68263e', '#4f3620', '#444718',
].map(c);

/**
 * The "Dodatne boje → Web" grid: 41 screen swatches — five neutrals and twelve
 * hues in a light, a mid and a deep tone. Bright on purpose; this is the tab a
 * shopper reaches for when the product is a screen mock-up or a digital file.
 * `text.webPalette` overrides it per product.
 */
export const DESIGN_WEB_PALETTE: Color[] = [
  '#000000', '#404040', '#808080', '#c0c0c0', '#ffffff',
  '#ff8a80', '#ff3b30', '#b71c1c',
  '#ffcc80', '#ff9500', '#e65100',
  '#fff59d', '#ffd400', '#f9a825',
  '#dcedc8', '#8bc34a', '#558b2f',
  '#a5d6a7', '#34c759', '#1b5e20',
  '#80cbc4', '#009688', '#00695c',
  '#81d4fa', '#00b0ff', '#01579b',
  '#90caf9', '#007aff', '#1a3fa0',
  '#b39ddb', '#5856d6', '#311b92',
  '#ce93d8', '#af52de', '#6a1b9a',
  '#f48fb1', '#ff2d55', '#880e4f',
  '#d7ccc8', '#8d6e63', '#4e342e',
].map(c);

/** Outline thickness presets, as a fraction of the font size. */
export const OUTLINE_WIDTHS = { none: 0, thin: 0.05, medium: 0.09, thick: 0.15 } as const;

export const DESIGN_TEXT_STYLES: DesignTextStyle[] = [
  { id: 'plain', name: 'Obično', color: c('#000000') },
  { id: 'white-black', name: 'Bela s crnim okvirom', color: c('#ffffff'), outline: { color: c('#000000'), width: OUTLINE_WIDTHS.medium } },
  { id: 'black-white', name: 'Crna s belim okvirom', color: c('#000000'), outline: { color: c('#ffffff'), width: OUTLINE_WIDTHS.medium } },
  { id: 'red-white', name: 'Crvena s belim okvirom', color: c('#e63946'), outline: { color: c('#ffffff'), width: OUTLINE_WIDTHS.medium } },
  { id: 'pink-wine', name: 'Roze s bordo okvirom', color: c('#ff8fab'), outline: { color: c('#8b1e3f'), width: OUTLINE_WIDTHS.thick } },
  { id: 'gold', name: 'Zlatna sa senkom', color: c('#f3c34b'), outline: { color: c('#8a5a00'), width: OUTLINE_WIDTHS.thin }, shadow: { color: c('#000000'), offset: 8, angle: 45, blur: 10 } },
  { id: 'sky', name: 'Plava s belim okvirom', color: c('#3a86ff'), outline: { color: c('#ffffff'), width: OUTLINE_WIDTHS.medium } },
  { id: 'shadowed', name: 'Bela sa senkom', color: c('#ffffff'), shadow: { color: c('#000000'), offset: 10, angle: 45, blur: 15 } },
];

export const DESIGN_AREA_DEFAULTS = {
  safeMarginMm: 0,
  bleedMm: 0,
  boundsMode: 'clip' as const,
  maxLayers: 20,
  text: { maxChars: 60, maxLines: 3, minSizeMm: 2, lineSpacing: 1.15, defaultText: 'Tvoj tekst' },
  // `blockBelowDpi` is parsed for older configs and ignored: nothing refuses a photo.
  image: { minDpi: 150, blockBelowDpi: 0, maxCount: 10 },
  placeholder: 'Dodaj tekst, sliku ili ukras',
} as const;

/** A config with every default filled in, for code that reads it. */
export interface ResolvedDesignAreaConfig {
  sticker?: DesignAreaConfig['sticker'];
  /** The shop's starter layouts, carried through so panels need not read the raw config. */
  templates: import('./designer-templates.js').DesignerTemplate[];
  sizeMm: { w: number; h: number };
  safeMarginMm: number;
  /** Trim allowance outside the zone, mm. A drawn guide only; 0 = none. */
  bleedMm: number;
  boundsMode: 'clip' | 'inside';
  allow: { text: boolean; image: boolean; artwork: boolean; emoji: boolean; shape: boolean; qr: boolean; background: boolean; pattern: boolean };
  maxLayers: number;
  text: {
    fontIds: string[];
    defaultFontId?: string;
    palette: Color[];
    printPalette: Color[];
    webPalette: Color[];
    allowCustomColor: boolean;
    outlinePalette: Color[];
    styles: DesignTextStyle[];
    maxChars: number;
    maxLines: number;
    allowCurve: boolean;
    allowShadow: boolean;
    defaultText: string;
    minSizeMm: number;
    maxSizeMm: number;
  };
  image: { minDpi: number; blockBelowDpi: number; allowRemoveBackground: boolean; allowFilters: boolean; maxCount: number };
  artwork: { libraryIds: string[]; allowRecolor: boolean };
  shape: { kinds: ShapeKind[]; palette: Color[] };
  pricing: { base: number; perText: number; perImage: number; perArtwork: number; perShape: number; max?: number };
  placeholder: string;
}

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/** Fills every default; tolerant of a partial or slightly wrong document. */
export function resolveDesignAreaConfig(config: DesignAreaConfig | undefined): ResolvedDesignAreaConfig {
  const cfg = config ?? { sizeMm: { w: 75, h: 50 } };
  const sizeMm = {
    w: Math.max(1, num(cfg.sizeMm?.w, 75)),
    h: Math.max(1, num(cfg.sizeMm?.h, 50)),
  };
  const palette = cfg.text?.palette?.length ? cfg.text.palette : DESIGN_PALETTE;
  const outlinePalette = cfg.text?.outlinePalette?.length
    ? cfg.text.outlinePalette
    : uniqueColors([c('#000000'), c('#ffffff'), ...palette]);
  const shorter = Math.min(sizeMm.w, sizeMm.h);
  return {
    sizeMm,
    ...(cfg.sticker ? { sticker: structuredClone(cfg.sticker) } : {}),
    // The starter layouts travel with the resolved config so a panel never has
    // to reach back into the raw document for half its data.
    templates: Array.isArray(cfg.templates) ? cfg.templates : [],
    safeMarginMm: Math.max(0, num(cfg.safeMarginMm, DESIGN_AREA_DEFAULTS.safeMarginMm)),
    bleedMm: Math.max(0, num(cfg.bleedMm, DESIGN_AREA_DEFAULTS.bleedMm)),
    boundsMode: cfg.boundsMode === 'inside' ? 'inside' : 'clip',
    allow: {
      text: cfg.allow?.text !== false,
      image: cfg.allow?.image !== false,
      artwork: cfg.allow?.artwork !== false,
      emoji: cfg.allow?.emoji !== false,
      shape: cfg.allow?.shape !== false,
      qr: cfg.allow?.qr !== false,
      background: cfg.allow?.background !== false,
      pattern: cfg.allow?.pattern !== false,
    },
    maxLayers: Math.max(1, Math.min(60, Math.round(num(cfg.maxLayers, DESIGN_AREA_DEFAULTS.maxLayers)))),
    text: {
      fontIds: (cfg.text?.fontIds ?? []).filter((id) => typeof id === 'string' && id),
      defaultFontId: cfg.text?.defaultFontId || undefined,
      palette,
      printPalette: cfg.text?.printPalette?.length ? cfg.text.printPalette : DESIGN_PRINT_SAFE_PALETTE,
      webPalette: cfg.text?.webPalette?.length ? cfg.text.webPalette : DESIGN_WEB_PALETTE,
      allowCustomColor: cfg.text?.allowCustomColor !== false,
      outlinePalette,
      styles: cfg.text?.styles?.length ? cfg.text.styles : DESIGN_TEXT_STYLES,
      maxChars: Math.max(1, Math.round(num(cfg.text?.maxChars, DESIGN_AREA_DEFAULTS.text.maxChars))),
      maxLines: Math.max(1, Math.round(num(cfg.text?.maxLines, DESIGN_AREA_DEFAULTS.text.maxLines))),
      allowCurve: cfg.text?.allowCurve !== false,
      allowShadow: cfg.text?.allowShadow !== false,
      defaultText: cfg.text?.defaultText || DESIGN_AREA_DEFAULTS.text.defaultText,
      minSizeMm: Math.max(0.5, num(cfg.text?.minSizeMm, DESIGN_AREA_DEFAULTS.text.minSizeMm)),
      maxSizeMm: Math.max(1, num(cfg.text?.maxSizeMm, shorter)),
    },
    image: {
      minDpi: Math.max(1, num(cfg.image?.minDpi, DESIGN_AREA_DEFAULTS.image.minDpi)),
      blockBelowDpi: Math.max(0, num(cfg.image?.blockBelowDpi, DESIGN_AREA_DEFAULTS.image.blockBelowDpi)),
      allowRemoveBackground: cfg.image?.allowRemoveBackground !== false,
      allowFilters: cfg.image?.allowFilters !== false,
      maxCount: Math.max(1, Math.round(num(cfg.image?.maxCount, DESIGN_AREA_DEFAULTS.image.maxCount))),
    },
    artwork: {
      libraryIds: (cfg.artwork?.libraryIds ?? []).filter((id) => typeof id === 'string' && id),
      allowRecolor: cfg.artwork?.allowRecolor !== false,
    },
    shape: {
      kinds: (cfg.shape?.kinds ?? []).filter(isShapeKind),
      palette: cfg.shape?.palette?.length ? cfg.shape.palette : palette,
    },
    pricing: {
      base: Math.max(0, num(cfg.pricing?.base, 0)),
      perText: Math.max(0, num(cfg.pricing?.perText, 0)),
      perImage: Math.max(0, num(cfg.pricing?.perImage, 0)),
      perArtwork: Math.max(0, num(cfg.pricing?.perArtwork, 0)),
      perShape: Math.max(0, num(cfg.pricing?.perShape, 0)),
      max: cfg.pricing?.max !== undefined ? Math.max(0, num(cfg.pricing.max, 0)) : undefined,
    },
    placeholder: cfg.placeholder || DESIGN_AREA_DEFAULTS.placeholder,
  };
}

function uniqueColors(colors: Color[]): Color[] {
  const seen = new Set<string>();
  return colors.filter((col) => {
    const key = normalizeHex(col.hex);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** `#rgb`/`#RRGGBB` → `#rrggbb`; empty string when not a colour. */
export function normalizeHex(hex: unknown): string {
  if (typeof hex !== 'string') return '';
  const raw = hex.trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(raw)) return `#${raw[0]}${raw[0]}${raw[1]}${raw[1]}${raw[2]}${raw[2]}`;
  if (/^[0-9a-f]{6}$/.test(raw)) return `#${raw}`;
  if (/^[0-9a-f]{8}$/.test(raw)) return `#${raw.slice(0, 6)}`;
  return '';
}

/** The physical size as the shopper reads it: "7,5 × 5 cm" / "50 × 30 mm". */
export function formatZoneSize(sizeMm: { w: number; h: number }, locale: 'sr' | 'en' = 'sr'): string {
  const sep = locale === 'sr' ? ',' : '.';
  const fmt = (mm: number, unit: 'cm' | 'mm'): string => {
    const value = unit === 'cm' ? mm / 10 : mm;
    const text = (Math.round(value * 10) / 10).toString();
    return text.replace('.', sep);
  };
  const unit: 'cm' | 'mm' = sizeMm.w >= 20 && sizeMm.h >= 20 ? 'cm' : 'mm';
  return `${fmt(sizeMm.w, unit)} × ${fmt(sizeMm.h, unit)} ${unit}`;
}

// ── Geometry ─────────────────────────────────────────────────────────────────

/** Everything needed to map a mm box in the zone onto this scene. */
export interface ZoneFrame {
  cx: number;
  cy: number;
  /** Scene px per mm on this scene (from the zone copy's own width). */
  pxPerMm: number;
  /** Zone rotation on this scene, degrees clockwise. */
  rotation: number;
  wMm: number;
  hMm: number;
  wPx: number;
  hPx: number;
}

export function zoneFrame(zone: Pick<DesignAreaElement, 'transform' | 'config'>): ZoneFrame {
  const cfg = resolveDesignAreaConfig(zone.config);
  const t = zone.transform;
  const pxPerMm = t.w > 0 ? t.w / cfg.sizeMm.w : t.h > 0 ? t.h / cfg.sizeMm.h : 1;
  return {
    cx: t.cx,
    cy: t.cy,
    pxPerMm,
    rotation: t.rotation ?? 0,
    wMm: cfg.sizeMm.w,
    hMm: cfg.sizeMm.h,
    wPx: cfg.sizeMm.w * pxPerMm,
    hPx: cfg.sizeMm.h * pxPerMm,
  };
}

/** The zone's axis-aligned rect on this scene (its rotated extent when tilted). */
export function zoneRect(frame: ZoneFrame): { cx: number; cy: number; w: number; h: number } {
  const rad = (frame.rotation * Math.PI) / 180;
  const cos = Math.abs(Math.cos(rad)), sin = Math.abs(Math.sin(rad));
  return {
    cx: frame.cx,
    cy: frame.cy,
    w: frame.wPx * cos + frame.hPx * sin,
    h: frame.wPx * sin + frame.hPx * cos,
  };
}

/** A zone-frame point (mm from the zone's top-left) → scene px. */
export function zonePoint(frame: ZoneFrame, xMm: number, yMm: number): Point {
  const dx = (xMm - frame.wMm / 2) * frame.pxPerMm;
  const dy = (yMm - frame.hMm / 2) * frame.pxPerMm;
  const rad = (frame.rotation * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  return { x: frame.cx + dx * cos - dy * sin, y: frame.cy + dx * sin + dy * cos };
}

/** The inverse: a scene point → zone frame mm. */
export function scenePointToZone(frame: ZoneFrame, x: number, y: number): { x: number; y: number } {
  const rad = (-frame.rotation * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const dx = x - frame.cx, dy = y - frame.cy;
  const lx = dx * cos - dy * sin, ly = dx * sin + dy * cos;
  return { x: lx / frame.pxPerMm + frame.wMm / 2, y: ly / frame.pxPerMm + frame.hMm / 2 };
}

/** A layer box (mm, zone frame) → the element transform on this scene. */
export function layerTransform(frame: ZoneFrame, box: DesignBox, flip?: { x?: boolean; y?: boolean }): Transform {
  const centre = zonePoint(frame, box.x, box.y);
  return {
    cx: centre.x,
    cy: centre.y,
    w: Math.max(0.01, box.w * frame.pxPerMm),
    h: Math.max(0.01, box.h * frame.pxPerMm),
    rotation: frame.rotation + (box.rotation ?? 0),
    ...(flip?.x ? { flipX: true } : {}),
    ...(flip?.y ? { flipY: true } : {}),
  };
}

/** The four corners of a rotated box, clockwise from top-left, in the box's own units. */
export function boxCorners(box: DesignBox): [Point, Point, Point, Point] {
  const rad = ((box.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const hw = box.w / 2, hh = box.h / 2;
  const corner = (dx: number, dy: number): Point => ({ x: box.x + dx * cos - dy * sin, y: box.y + dx * sin + dy * cos });
  return [corner(-hw, -hh), corner(hw, -hh), corner(hw, hh), corner(-hw, hh)];
}

/** Axis-aligned extent of a (rotated) box, in its own units. */
export function boxExtent(box: DesignBox): { left: number; top: number; right: number; bottom: number } {
  const corners = boxCorners(box);
  const xs = corners.map((p) => p.x), ys = corners.map((p) => p.y);
  return { left: Math.min(...xs), top: Math.min(...ys), right: Math.max(...xs), bottom: Math.max(...ys) };
}

// ── Perspective inheritance (mockup copies drawn through a homography) ───────

type Mat3 = [number, number, number, number, number, number, number, number, number];

/**
 * The projective map taking the unit square's corners (clockwise from
 * top-left) onto `dst`. Solved with the standard 8×8 system; `undefined` for
 * a degenerate quad.
 */
function unitSquareTo(dst: [Point, Point, Point, Point]): Mat3 | undefined {
  const src: [Point, Point, Point, Point] = [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];
  const a: number[][] = [];
  const b: number[] = [];
  for (let i = 0; i < 4; i++) {
    const { x, y } = src[i]!, { x: u, y: v } = dst[i]!;
    a.push([x, y, 1, 0, 0, 0, -u * x, -u * y]); b.push(u);
    a.push([0, 0, 0, x, y, 1, -v * x, -v * y]); b.push(v);
  }
  const h = solve(a, b);
  if (!h) return undefined;
  return [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!, 1];
}

function solve(a: number[][], b: number[]): number[] | undefined {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i]!]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) if (Math.abs(m[r]![col]!) > Math.abs(m[pivot]![col]!)) pivot = r;
    if (Math.abs(m[pivot]![col]!) < 1e-12) return undefined;
    [m[col], m[pivot]] = [m[pivot]!, m[col]!];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = m[r]![col]! / m[col]![col]!;
      for (let k = col; k <= n; k++) m[r]![k]! -= f * m[col]![k]!;
    }
  }
  return m.map((row, i) => row[n]! / row[i]!);
}

function applyMat(h: Mat3, p: Point): Point {
  const w = h[6] * p.x + h[7] * p.y + h[8];
  return { x: (h[0] * p.x + h[1] * p.y + h[2]) / w, y: (h[3] * p.x + h[4] * p.y + h[5]) / w };
}

/**
 * Where a child box's corners land when the whole zone is drawn through the
 * zone copy's `perspective`. The zone's unrotated box maps onto its four
 * corners; the child's corners are expressed in that box and pushed through.
 */
export function projectThroughZone(
  frame: ZoneFrame,
  zoneCorners: [Point, Point, Point, Point],
  box: DesignBox,
): [Point, Point, Point, Point] | undefined {
  const h = unitSquareTo(zoneCorners);
  if (!h) return undefined;
  const corners = boxCorners(box).map((p) => applyMat(h, { x: p.x / frame.wMm, y: p.y / frame.hMm }));
  return corners as [Point, Point, Point, Point];
}

// ── Curved text ──────────────────────────────────────────────────────────────

/**
 * Turns the shopper's bend slider into an ellipse arc the renderer already
 * knows how to walk.
 *
 * `bend` ±100 is a semicircle; the arc length equals the straight text width
 * so the letters keep their spacing while the line bends. Positive arches up
 * (glyphs on top of the circle, reading clockwise), negative hangs down
 * (glyphs under the circle, reading counter-clockwise so the text still
 * reads left to right). Returns the curve and where the ellipse centre sits
 * relative to the text box's centre, so `expandDesignLayer` can position it.
 */
export function bendCurve(
  sizePx: number,
  textWidthPx: number,
  bend: number,
  boxHeightPx: number,
): { curve: TextCurve; centreOffsetY: number } | undefined {
  const amount = Math.max(-100, Math.min(100, bend));
  if (!Number.isFinite(amount) || Math.abs(amount) < 1) return undefined;
  const theta = (Math.PI * Math.abs(amount)) / 100; // radians of sweep
  const length = Math.max(textWidthPx, sizePx);
  const radius = Math.max(sizePx * 0.6, length / theta);
  const half = ((theta / 2) * 180) / Math.PI;
  if (amount > 0) {
    // Baseline apex 0.8 em under the box top; centre is one radius below it.
    const centreOffsetY = -boxHeightPx / 2 + sizePx * 0.8 + radius;
    return {
      curve: { kind: 'ellipse', horizontalDiameter: radius * 2, verticalDiameter: radius * 2, startAngle: -half, endAngle: half, convex: true },
      centreOffsetY,
    };
  }
  // Lowest baseline point 0.25 em above the box bottom; centre one radius above.
  const centreOffsetY = boxHeightPx / 2 - sizePx * 0.25 - radius;
  return {
    curve: { kind: 'ellipse', horizontalDiameter: radius * 2, verticalDiameter: radius * 2, startAngle: 180 + half, endAngle: 180 - half, convex: true },
    centreOffsetY,
  };
}

/**
 * The smallest a fixed box may be, as a multiple of the em.
 *
 * One em: narrower than that and even a single letter has to break, so the
 * "wrap" degenerates into a column of characters nobody asked for.
 */
export const FIXED_TEXT_BOX_MIN_EM = 1;

/**
 * How tall a fixed box must be to hold `lines` wrapped lines — the designer's
 * side handles set the width, and this is the height that follows.
 *
 * It is the renderer's own line box, not an approximation. `expandDesignLayer`
 * hands the paragraph a `heightMultiplier` of `lineSpacing`, which makes Skia
 * override the font's metrics so that every line box is exactly
 * `fontSize × lineSpacing` tall; the ink the renderer then aligns on runs from
 * the first line's ascent to the last line's descent, which is `lines` of those
 * boxes and nothing more. Measured against real Skia (Montserrat Bold, sizes
 * 40 px, spacings 1 / 1.15 / 1.5, one to six wrapped lines): the ink height came
 * back as `lines × size × spacing` to the last decimal. There is therefore no
 * padding term — adding one would leave an anchored box with a strip of nothing
 * at the bottom, which is precisely what "Anchor Bottom" must not draw.
 */
export function fixedTextBoxHeightMm(
  lines: number,
  sizeMm: number,
  lineSpacing: number = DESIGN_AREA_DEFAULTS.text.lineSpacing,
): number {
  const rows = Math.max(1, Math.round(Number.isFinite(lines) ? lines : 1));
  const spacing = Number.isFinite(lineSpacing) ? Math.min(3, Math.max(0.5, lineSpacing)) : DESIGN_AREA_DEFAULTS.text.lineSpacing;
  return round(rows * Math.max(0, sizeMm) * spacing);
}

/** The box a bent line of text needs (chord width, sagitta + em), in the same units as its inputs. */
export function bentTextBox(size: number, textWidth: number, bend: number): { w: number; h: number } {
  const amount = Math.max(-100, Math.min(100, bend));
  if (Math.abs(amount) < 1) return { w: textWidth, h: size * 1.15 };
  const theta = (Math.PI * Math.abs(amount)) / 100;
  const length = Math.max(textWidth, size);
  const radius = Math.max(size * 0.6, length / theta);
  const chord = 2 * radius * Math.sin(Math.min(theta, Math.PI) / 2);
  const sagitta = radius * (1 - Math.cos(Math.min(theta, Math.PI) / 2));
  return { w: Math.max(chord, size) + size * 0.4, h: sagitta + size * 1.15 };
}

// ── Expansion ────────────────────────────────────────────────────────────────

export interface ExpandOptions {
  /**
   * Which renderer target this scene is for. Preview scenes always clip the
   * layers to the zone; print scenes clip or demand `inside` per config.
   */
  target?: 'print' | 'preview';
  /**
   * Layers the EDITOR is hiding right now (the eye in the Slojevi panel),
   * as opposed to `layer.visible === false`, which is a persistent choice that
   * travels with the answer. Both are honoured the same way here: the layer is
   * not drawn, and — this is the part that matters — it is not part of a
   * sticker's backing, so the white contour follows what the shopper can see.
   */
  hiddenLayerIds?: ReadonlySet<string> | readonly string[];
}

/** One predicate for both kinds of hiding, so no caller can honour only one. */
function visibleLayers(layers: DesignLayer[], hidden: ReadonlySet<string>): DesignLayer[] {
  return layers.filter((layer) => layer.visible !== false && !hidden.has(layer.id));
}

function hiddenSet(options: ExpandOptions): ReadonlySet<string> {
  const ids = options.hiddenLayerIds;
  if (!ids) return EMPTY_IDS;
  return ids instanceof Set ? ids : new Set(ids as readonly string[]);
}

const EMPTY_IDS: ReadonlySet<string> = new Set<string>();

/** The element id of a layer's expansion: `<zone id>:<layer id>`. */
export function expandedLayerId(zoneId: string, layerId: string): string {
  return `${zoneId}:${layerId}`;
}

/** Splits an expanded id back into zone and layer ids; undefined for a plain element. */
export function parseExpandedId(id: string): { zoneId: string; layerId: string } | undefined {
  const at = id.indexOf(':');
  if (at <= 0 || at === id.length - 1) return undefined;
  return { zoneId: id.slice(0, at), layerId: id.slice(at + 1) };
}

function layerName(layer: DesignLayer): string {
  if (layer.name) return layer.name;
  switch (layer.kind) {
    case 'text': return `Tekst: ${layer.text.split('\n')[0]?.slice(0, 24) ?? ''}`;
    case 'image': return 'Slika';
    case 'artwork': return 'Ukras';
    case 'shape': return `Oblik: ${shapeDef(layer.shape).labelSr}`;
    case 'qr': return `QR: ${layer.url}`;
  }
}

function mapEffects(effects: DesignImageEffect[] | undefined, cfg: ResolvedDesignAreaConfig): ImageEffect[] | undefined {
  if (!effects?.length) return undefined;
  const out: ImageEffect[] = [];
  for (const effect of effects) {
    if (effect.kind === 'removeBackground') { if (cfg.image.allowRemoveBackground) out.push({ kind: 'removeBackground' }); continue; }
    if (!cfg.image.allowFilters) continue;
    if (effect.kind === 'grayscale' || effect.kind === 'sepia') out.push({ kind: effect.kind });
    if (effect.kind === 'whiteTransparent') out.push(effect.connected ? { kind: 'whiteTransparent', connected: true } : { kind: 'whiteTransparent' });
    if (effect.kind === 'look') {
      const look = designerLook(effect.id);
      if (look) out.push({ kind: 'colorMatrix', matrix: [...look.matrix] });
    }
    if (effect.kind === 'brightness' || effect.kind === 'contrast') out.push({ kind: effect.kind, amount: effect.amount });
  }
  return out.length ? out : undefined;
}

/** Axis-aligned extent of a set of layers, in zone millimetres. */
export function layersExtent(layers: DesignLayer[]): { left: number; top: number; right: number; bottom: number } {
  const boxes = layers.map((layer) => boxExtent(layer.box));
  return {
    left: Math.min(...boxes.map((b) => b.left)),
    top: Math.min(...boxes.map((b) => b.top)),
    right: Math.max(...boxes.map((b) => b.right)),
    bottom: Math.max(...boxes.map((b) => b.bottom)),
  };
}

/**
 * True when any corner of the box crosses the zone edge.
 *
 * The 0.01 mm slack is not decoration: a layer fitted to the zone lands on the
 * edge to within floating-point noise, and without it "Uklopi" would report the
 * design it just made as broken.
 */
export function boxOverhangs(size: { w: number; h: number }, box: DesignBox): boolean {
  const extent = boxExtent(box);
  return extent.left < -0.01 || extent.top < -0.01 || extent.right > size.w + 0.01 || extent.bottom > size.h + 0.01;
}

/**
 * One layer → one native element on this scene. Pure: no fonts, no assets,
 * no measurement — everything it needs travels in the layer.
 */
export function expandDesignLayer(
  zone: DesignAreaElement,
  layer: DesignLayer,
  index: number,
  options: ExpandOptions = {},
): SceneElement | undefined {
  const cfg = resolveDesignAreaConfig(zone.config);
  const frame = zoneFrame(zone);
  const total = zone.value?.layers.length ?? index + 1;
  const base = {
    id: expandedLayerId(zone.id, layer.id),
    name: layerName(layer),
    // Children sit just above the zone, in their own order, below whatever the author stacked next.
    z: zone.z + ((index + 1) / (total + 1)) * 0.5,
    transform: layerTransform(frame, layer.box, { x: layer.flipX, y: layer.flipY }),
    ...(layer.opacity !== undefined && layer.opacity < 1 ? { opacity: Math.max(0, layer.opacity) } : {}),
    ...(zone.visible === false || layer.visible === false ? { visible: false } : {}),
    ...(zone.previewOnly ? { previewOnly: true } : {}),
    ...(zone.printOnly ? { printOnly: true } : {}),
    ...(zone.fromDesign ? { fromDesign: true } : {}),
    ...(zone.groupId ? { groupId: zone.groupId } : {}),
  };
  const rect = zoneRect(frame);
  const mode: Bounds['mode'] = options.target === 'print' && cfg.boundsMode === 'inside' ? 'inside' : 'clip';
  const bounds: Bounds = { mode, rect };
  const perspective = zone.perspective
    ? (() => {
      const corners = projectThroughZone(frame, zone.perspective!.corners, layer.box);
      return corners ? { perspective: { corners } } : {};
    })()
    : {};

  switch (layer.kind) {
    case 'text': {
      const sizePx = layer.sizeMm * frame.pxPerMm;
      // A fixed box is the shopper's own width, and the renderer wraps a
      // multiline element at exactly the element width — which `layerTransform`
      // took straight from `layer.box.w`. So the whole of "fixed width, text
      // wraps, height follows the lines" is these two flags plus the box.
      // Vertical text is stacked glyphs, a layout of its own; it is never fixed.
      const fixed = layer.boxMode === 'fixed' && layer.orientation !== 'vertical';
      const el: TextElement = {
        ...base,
        type: 'text',
        tool: layer.bend && !fixed ? 'curved' : 'box',
        value: layer.orientation === 'vertical' ? designerVerticalText(layer.text) : layer.text,
        font: { fontId: layer.fontId, size: sizePx, minSize: sizePx, maxSize: sizePx },
        color: layer.color,
        align: layer.align ?? 'center',
        // Zazzle's anchor. 'middle' is our default and the renderer's word for
        // it is 'center'.
        verticalAlign: layer.anchorV === 'top' ? 'top' : layer.anchorV === 'bottom' ? 'bottom' : 'center',
        multiline: fixed || layer.orientation === 'vertical' || layer.text.includes('\n'),
        lineSpacing: layer.lineSpacing ?? DESIGN_AREA_DEFAULTS.text.lineSpacing,
        ...(layer.tracking ? { tracking: layer.tracking } : {}),
        ...(layer.uppercase ? { uppercase: true } : {}),
        ...(layer.outline && layer.outline.width > 0
          ? { outline: { color: layer.outline.color, width: layer.outline.width * sizePx } }
          : {}),
        ...(layer.shadow && cfg.text.allowShadow
          ? { shadow: { ...layer.shadow, relative: true } }
          : {}),
        bounds,
        ...perspective,
      };
      // A bend needs one straight line to wrap around a circle; a fixed box may
      // hold several, so the box wins and the curve is ignored. The normaliser
      // drops one of the two before it ever gets here — this is the guard for a
      // scene composed by hand.
      if (layer.bend && cfg.text.allowCurve && !fixed) {
        const widthPx = (layer.textWidthMm ?? layer.box.w) * frame.pxPerMm;
        const bent = bendCurve(sizePx, widthPx, layer.bend, layer.box.h * frame.pxPerMm);
        if (bent) {
          // The ellipse centre is the element centre: shift it down/up from the box centre, along the box's own axis.
          const rad = (base.transform.rotation! * Math.PI) / 180;
          el.transform = {
            ...el.transform,
            cx: el.transform.cx - bent.centreOffsetY * Math.sin(rad),
            cy: el.transform.cy + bent.centreOffsetY * Math.cos(rad),
          };
          el.curve = bent.curve;
        }
      }
      return el;
    }
    case 'image': {
      const el: ImageElement = {
        ...base,
        type: 'image',
        source: { kind: 'static', assetId: layer.assetId },
        value: layer.assetId,
        fit: layer.fit ?? 'cover',
        ...(layer.maskShape ? { clipShape: layer.maskShape } : {}),
        ...(layer.erase?.length ? { erase: layer.erase } : {}),
        ...(layer.crop ? { crop: layer.crop } : {}),
        ...(mapEffects(layer.effects, cfg) ? { effects: mapEffects(layer.effects, cfg) } : {}),
        bounds,
        ...perspective,
      };
      return el;
    }
    case 'artwork': {
      if (layer.vector) {
        const el: VectorElement = {
          ...base,
          type: 'vector',
          source: { kind: 'static', assetId: layer.assetId },
          value: layer.assetId,
          fit: 'contain',
          ...(cfg.artwork.allowRecolor && layer.singleColor ? { singleColor: layer.singleColor } : {}),
          ...(cfg.artwork.allowRecolor && layer.colorMap?.length ? { colorMap: layer.colorMap } : {}),
          bounds,
          ...perspective,
        };
        return el;
      }
      const el: ImageElement = {
        ...base,
        type: 'image',
        source: { kind: 'static', assetId: layer.assetId },
        value: layer.assetId,
        fit: 'contain',
        ...(cfg.artwork.allowRecolor && layer.colorize ? { colorize: layer.colorize, colorizeMode: 'alpha' } : {}),
        bounds,
        ...perspective,
      };
      return el;
    }
    case 'qr': {
      const side = Math.min(base.transform.w, base.transform.h);
      // A seventh of the side is comfortably more than the four-module quiet
      // zone the spec demands, at every payload length we accept. `inside`
      // because a QR clipped on one edge is a QR that will not scan.
      return {
        ...base,
        type: 'qr',
        value: layer.url,
        errorCorrection: 'M',
        foreground: { hex: '#000000' },
        background: { hex: '#ffffff' },
        transparentBackground: false,
        margin: side / 7,
        bounds: { ...bounds, mode: 'inside' },
        ...perspective,
      };
    }
    case 'shape': {
      const el: ShapeElement = {
        ...base,
        type: 'shape',
        shape: layer.shape,
        ...(layer.fill ? { fill: layer.fill } : {}),
        ...(layer.stroke && layer.stroke.widthMm > 0
          ? { stroke: { color: layer.stroke.color, width: layer.stroke.widthMm * frame.pxPerMm } }
          : {}),
        bounds,
        ...perspective,
      };
      return el;
    }
    default:
      return undefined;
  }
}

/** True when the scene carries at least one zone. */
export function hasDesignAreas(scene: Scene): boolean {
  return scene.elements.some((el) => el.type === 'designArea');
}

export function designAreasOf(scene: Scene): DesignAreaElement[] {
  return scene.elements.filter((el): el is DesignAreaElement => el.type === 'designArea');
}

/**
 * Replaces every zone by its shopper's layers. A zone with no answer (or an
 * empty one) vanishes — it was never something to print. Idempotent on a
 * scene without zones, so every resolver can call it unconditionally.
 */
export function expandDesignAreas(scene: Scene, options: ExpandOptions = {}): Scene {
  if (!hasDesignAreas(scene)) return scene;
  const target = options.target ?? (scene.target === 'preview' ? 'preview' : 'print');
  const hidden = hiddenSet(options);
  const elements: SceneElement[] = [];
  for (const el of scene.elements) {
    if (el.type !== 'designArea') { elements.push(el); continue; }
    const layers = el.value?.layers ?? [];
    const shown = visibleLayers(layers, hidden);

    // A sticker zone stays ONE element: the renderer needs the whole design at
    // once to trace a single contour around it. It gets only the layers that
    // are actually visible, so a hidden layer can never push the white backing
    // out to the edge of a design nobody can see. With nothing left to show
    // there is no sticker to cut, and the zone falls through to plain layers.
    if (shown.length && el.value?.sticker && el.config.sticker?.allowed) {
      elements.push({ ...el, type: 'sticker', value: { ...el.value, layers: shown } });
      continue;
    }

    if (el.value?.background) {
      // The printed background is a full-zone rectangle under everything else.
      // `inside` rather than `clip` keeps it a native vector rect in exports —
      // it lines up with the zone by construction, so there is nothing to clip.
      const backgroundLayer: DesignShapeLayer = {
        id: '__background',
        kind: 'shape',
        shape: 'rect',
        fill: el.value.background,
        box: { x: el.config.sizeMm.w / 2, y: el.config.sizeMm.h / 2, ...el.config.sizeMm },
      };
      const background = expandDesignLayer(el, backgroundLayer, -1, { target });
      if (background) {
        if (background.bounds && !el.perspective) background.bounds.mode = 'inside';
        elements.push(background);
      }
    }

    layers.forEach((layer, index) => {
      // Editor-hidden layers keep their slot in the z ladder and simply do not
      // paint, exactly like a layer the shopper switched off for good.
      const invisible = hidden.has(layer.id);
      const tiles = designerPatternTiles(layer, el.config.sizeMm);
      // A pattern always clips: its tiles are meant to run off every edge.
      const zone = layer.pattern ? { ...el, config: { ...el.config, boundsMode: 'clip' as const } } : el;
      tiles.forEach((tile, tileIndex) => {
        const expanded = expandDesignLayer(zone, tile, index, { target });
        if (!expanded) return;
        if (invisible) expanded.visible = false;
        if (layer.pattern) relaxInteriorTileBounds(expanded, tile, el);
        // Tiles of one layer share an index, so nudge them apart to keep the
        // paint order stable without disturbing the layer's place in the stack.
        expanded.z += tileIndex * 1e-8;
        elements.push(expanded);
      });
    });
  }
  return { ...scene, elements };
}

/**
 * A pattern tile that lies wholly inside the zone needs no clipping, and a
 * shape that is not clipped stays a vector path in the SVG/PDF export instead
 * of being flattened to pixels. Edge tiles keep the renderer's clip policy.
 */
function relaxInteriorTileBounds(expanded: SceneElement, tile: DesignLayer, zone: DesignAreaElement): void {
  if (tile.kind !== 'shape' || zone.perspective || !expanded.bounds) return;
  const extent = boxExtent(tile.box);
  const pad = (tile.stroke?.widthMm ?? 0) / 2;
  const inside = extent.left >= pad && extent.top >= pad
    && extent.right <= zone.config.sizeMm.w - pad && extent.bottom <= zone.config.sizeMm.h - pad;
  if (inside) expanded.bounds.mode = 'inside';
}

// ── Standalone scenes for the designer ───────────────────────────────────────

/**
 * A scene that IS the zone: canvas = the zone at `pxPerMm`, transparent
 * background, the layers expanded into it. The designer renders this for its
 * working canvas and for the cart card thumbnail; a single-layer variant
 * (`layerScene`) gives the per-layer rasters it drags around.
 */
export function zoneScene(
  config: DesignAreaConfig,
  answer: DesignAreaAnswer | null,
  pxPerMm: number,
  options: { id?: string; dpi?: number; clip?: boolean; hiddenLayerIds?: ExpandOptions['hiddenLayerIds'] } = {},
): Scene {
  const cfg = resolveDesignAreaConfig(config);
  const w = cfg.sizeMm.w * pxPerMm, h = cfg.sizeMm.h * pxPerMm;
  const zone: DesignAreaElement = {
    id: options.id ?? 'zone',
    type: 'designArea',
    z: 0,
    transform: { cx: w / 2, cy: h / 2, w, h },
    config,
    value: answer,
  };
  const scene: Scene = {
    schemaVersion: 1,
    id: `${zone.id}-scene`,
    target: 'preview',
    width: w,
    height: h,
    dpi: options.dpi ?? (pxPerMm * 25.4),
    elements: [zone],
  };
  const expanded = expandDesignAreas(scene, { target: 'preview', hiddenLayerIds: options.hiddenLayerIds });
  if (options.clip === false) {
    expanded.elements = expanded.elements.map((el) => ({ ...el, bounds: undefined }));
  }
  return expanded;
}

/**
 * One layer alone on a canvas that fits it plus `padMm` all round, centred
 * and unrotated (rotation is the designer's CSS transform). Same expansion,
 * same renderer — what the shopper drags is what will print.
 */
export function layerScene(
  config: DesignAreaConfig,
  layer: DesignLayer,
  pxPerMm: number,
  padMm = 2,
): Scene {
  const w = (layer.box.w + padMm * 2) * pxPerMm;
  const h = (layer.box.h + padMm * 2) * pxPerMm;
  // Upright, centred, unrepeated and always shown: this raster is the thing the
  // shopper drags, and the designer supplies rotation and hiding itself.
  const local: DesignLayer = { ...layer, pattern: undefined, visible: true, box: { ...layer.box, x: layer.box.w / 2 + padMm, y: layer.box.h / 2 + padMm, rotation: 0 } };
  const zoneCfg: DesignAreaConfig = { ...config, sizeMm: { w: w / pxPerMm, h: h / pxPerMm }, boundsMode: 'clip' };
  const zone: DesignAreaElement = {
    id: 'layer',
    type: 'designArea',
    z: 0,
    transform: { cx: w / 2, cy: h / 2, w, h },
    config: zoneCfg,
    value: { v: DESIGN_ANSWER_VERSION, layers: [local] },
  };
  const scene: Scene = {
    schemaVersion: 1,
    id: `layer-${layer.id}`,
    target: 'preview',
    width: w,
    height: h,
    dpi: pxPerMm * 25.4,
    elements: [zone],
  };
  const expanded = expandDesignAreas(scene, { target: 'preview' });
  // Nothing to clip against on a canvas made for the layer.
  expanded.elements = expanded.elements.map((el) => ({ ...el, bounds: undefined }));
  return expanded;
}

// ── Resolution ───────────────────────────────────────────────────────────────

/**
 * Pixels per inch a photo will print at inside its box. `crop.zoom` shows a
 * smaller window of the source, so the visible pixels shrink with it.
 */
export function effectiveDpi(sourcePx: { w: number; h: number }, box: DesignBox, crop?: ImageCrop, fit: 'cover' | 'contain' = 'cover'): number {
  if (sourcePx.w <= 0 || sourcePx.h <= 0 || box.w <= 0 || box.h <= 0) return 0;
  const scale = fit === 'contain'
    ? Math.min(box.w / sourcePx.w, box.h / sourcePx.h)
    : coverPhotoFrame(sourcePx, box, crop).scale;
  return 25.4 / scale;
}

export type DesignQuality = 'high' | 'mid' | 'low';

/**
 * How a photo will print, in three words the panel shows beside the dpi.
 *
 * There is no fourth, refusing word: the shop always lets the shopper order
 * the picture they have (2026-09-16). `blockBelowDpi` in the config is kept so
 * older configs still parse, but nothing reads it any more.
 */
export function qualityFor(dpi: number, cfg: ResolvedDesignAreaConfig): DesignQuality {
  if (dpi < cfg.image.minDpi) return 'low';
  if (dpi < cfg.image.minDpi * 2) return 'mid';
  return 'high';
}

// ── Validation ───────────────────────────────────────────────────────────────

export type DesignProblemCode =
  | 'bad-answer'
  | 'too-many-layers'
  | 'kind-not-allowed'
  | 'bad-geometry'
  | 'outside-zone'
  | 'text-empty'
  | 'text-too-long'
  | 'too-many-lines'
  | 'font-not-allowed'
  | 'color-not-allowed'
  | 'size-out-of-range'
  | 'too-many-images'
  | 'low-resolution'
  | 'library-not-allowed'
  | 'shape-not-allowed'
  | 'asset-unknown'
  /** A `mustReplace` template layer still carries the authored content. */
  | 'template-unreplaced'
  /** The sticker's white border runs off the zone (needs `ctx.stickerContour`). */
  | 'sticker-edge'
  /** The sticker falls into more separate pieces than a cutter can bridge. */
  | 'sticker-islands';

export interface DesignProblem {
  code: DesignProblemCode;
  layerId?: string;
  detail: string;
}

export interface DesignValidationContext {
  /** Every font id the shop has; absent = not checked. */
  fontIds?: ReadonlySet<string>;
  /**
   * What is known about an asset id; `undefined` = unknown asset. `pack` is
   * the built-in pack the asset's library came from (`pack:emoji`), which is
   * how the emoji allowance is told apart from ordinary artwork.
   */
  asset?: (assetId: string) => { px?: { w: number; h: number }; libraryId?: string; vector?: boolean; kind?: string; pack?: string } | undefined;
  /** Libraries offered when the zone names none (the built-in packs). */
  storefrontLibraryIds?: ReadonlySet<string>;
  /** Library id → built-in pack id, for the emoji allowance when `asset` is absent. */
  libraryPacks?: ReadonlyMap<string, string>;
  /**
   * A caller that can raster the design (the widget, which owns a renderer)
   * hands back the sticker contour it already traced for the canvas, and gets
   * `sticker-edge` / `sticker-islands` as ordinary blocking problems — surfaced
   * while the shopper is still designing instead of at the till.
   *
   * `undefined` means "not traced yet"; nothing is reported then. The API and
   * the worker pass no hook at all and keep failing hard in the cut pipeline,
   * which is the check that actually guards production.
   */
  stickerContour?: (answer: DesignAreaAnswer) => { touchesEdge: boolean; islands: number } | undefined;
}

function sameColor(a: Color, b: Color): boolean {
  return normalizeHex(a.hex) === normalizeHex(b.hex);
}

function colorAllowed(color: Color, palette: Color[], allowCustom: boolean): boolean {
  if (!normalizeHex(color.hex)) return false;
  return allowCustom || palette.some((p) => sameColor(p, color));
}

/**
 * Everything wrong with an answer against a zone's rules. Empty = accepted.
 * Shared by the designer (to explain) and the API (to refuse); both must
 * agree or a shopper is told "fine" and then refused at the till.
 */
export function designAreaProblems(
  config: DesignAreaConfig,
  answer: DesignAreaAnswer | null | undefined,
  ctx: DesignValidationContext = {},
): DesignProblem[] {
  const cfg = resolveDesignAreaConfig(config);
  const problems: DesignProblem[] = [];
  if (!answer) return problems;
  if (answer.v !== DESIGN_ANSWER_VERSION || !Array.isArray(answer.layers)) {
    return [{ code: 'bad-answer', detail: 'unknown answer shape' }];
  }
  if (answer.layers.length > cfg.maxLayers) {
    problems.push({ code: 'too-many-layers', detail: `${answer.layers.length} > ${cfg.maxLayers}` });
  }
  if (answer.sticker && (!config.sticker?.allowed || !validStickerSettings(answer.sticker))) {
    problems.push({ code: 'bad-answer', detail: 'Sticker is unavailable or its border/bridge dimensions are invalid' });
  }
  if (config.sticker?.required && answer.layers.length && !answer.sticker) {
    problems.push({ code: 'bad-answer', detail: 'This product requires Sticker mode' });
  }
  if (answer.background && (!cfg.allow.background || !colorAllowed(answer.background, cfg.text.palette, cfg.text.allowCustomColor))) {
    problems.push({ code: 'color-not-allowed', detail: 'Background is unavailable or its colour is not allowed' });
  }
  // A printed backdrop and a die-cut contour describe two different products:
  // one is ink to the zone edge, the other is a shape cut around the artwork.
  if (answer.sticker && answer.background) {
    problems.push({ code: 'bad-answer', detail: 'Clear the printed background before enabling Sticker' });
  }
  if (answer.sticker) problems.push(...stickerShapeProblems(answer, ctx));
  const allowedLibraries = cfg.artwork.libraryIds.length ? new Set(cfg.artwork.libraryIds) : ctx.storefrontLibraryIds;
  let images = 0;
  const seen = new Set<string>();
  let tiles = 0;
  for (const layer of answer.layers) {
    const id = layer.id;
    if (typeof id !== 'string' || !id || seen.has(id)) {
      problems.push({ code: 'bad-answer', layerId: id, detail: 'layer id missing or repeated' });
      continue;
    }
    seen.add(id);
    const box = layer.box;
    if (layer.name !== undefined && (typeof layer.name !== 'string' || layer.name.length > LAYER_NAME_MAX)) {
      problems.push({ code: 'bad-answer', layerId: id, detail: `Layer name is limited to ${LAYER_NAME_MAX} characters` });
    }
    problems.push(...templateProblems(layer));
    if (layer.pattern) {
      // One layer's repeat is capped inside `designerPatternTiles`; `tiles`
      // carries the running total so a zone cannot be filled by stacking many
      // cheap repeats until the renderer has a hundred thousand elements.
      try {
        if (!cfg.allow.pattern || layer.kind === 'qr') throw new Error('Pattern is unavailable for this layer');
        tiles += designerPatternTiles(layer, cfg.sizeMm).length;
        if (tiles > MAX_TILES_PER_ZONE) throw new Error(`Design exceeds ${MAX_TILES_PER_ZONE} repeated tiles`);
      } catch (error) {
        problems.push({ code: 'bad-answer', layerId: id, detail: error instanceof Error ? error.message : 'Invalid pattern' });
      }
    }
    if (!box || ![box.x, box.y, box.w, box.h].every((v) => typeof v === 'number' && Number.isFinite(v)) || box.w <= 0 || box.h <= 0) {
      problems.push({ code: 'bad-geometry', layerId: id, detail: 'box must be finite and positive' });
      continue;
    }
    if (box.w > cfg.sizeMm.w * 4 || box.h > cfg.sizeMm.h * 4) {
      problems.push({ code: 'bad-geometry', layerId: id, detail: 'layer far larger than the zone' });
    }
    const extent = boxExtent(box);
    const outside = extent.right < 0 || extent.bottom < 0 || extent.left > cfg.sizeMm.w || extent.top > cfg.sizeMm.h;
    const overhang = boxOverhangs(cfg.sizeMm, box);
    if (outside || (cfg.boundsMode === 'inside' && overhang && !layer.pattern)) {
      problems.push({ code: 'outside-zone', layerId: id, detail: outside ? 'entirely outside the zone' : 'crosses the zone edge' });
    }
    switch (layer.kind) {
      case 'text': {
        if (layer.orientation === 'vertical' && layer.bend) problems.push({code:'bad-answer',layerId:id,detail:'Vertical text cannot be curved'});
        // A fixed box wraps at its own width, so that width has to be able to
        // hold something: under one em even a single letter breaks, and the
        // shopper gets a column of characters instead of a line of words.
        if (layer.boxMode === 'fixed' && typeof layer.sizeMm === 'number' && box.w < layer.sizeMm * FIXED_TEXT_BOX_MIN_EM - 1e-6) {
          problems.push({ code: 'bad-geometry', layerId: id, detail: `A fixed text box must be at least one em wide (${round(layer.sizeMm * FIXED_TEXT_BOX_MIN_EM, 1)} mm)` });
        }
        if (!cfg.allow.text) { problems.push({ code: 'kind-not-allowed', layerId: id, detail: 'text' }); break; }
        const text = typeof layer.text === 'string' ? layer.text : '';
        if (!text.trim()) problems.push({ code: 'text-empty', layerId: id, detail: 'no text' });
        // Characters as the shopper sees them: an emoji is one, however many code points it takes.
        if (textLength(text) > cfg.text.maxChars) problems.push({ code: 'text-too-long', layerId: id, detail: `${textLength(text)} > ${cfg.text.maxChars}` });
        if (text.split('\n').length > cfg.text.maxLines) problems.push({ code: 'too-many-lines', layerId: id, detail: `> ${cfg.text.maxLines} lines` });
        if (cfg.text.fontIds.length && !cfg.text.fontIds.includes(layer.fontId)) {
          problems.push({ code: 'font-not-allowed', layerId: id, detail: layer.fontId });
        } else if (ctx.fontIds && !ctx.fontIds.has(layer.fontId)) {
          problems.push({ code: 'font-not-allowed', layerId: id, detail: `unknown font ${layer.fontId}` });
        }
        if (!layer.color || !colorAllowed(layer.color, cfg.text.palette, cfg.text.allowCustomColor)) {
          problems.push({ code: 'color-not-allowed', layerId: id, detail: 'fill' });
        }
        if (layer.outline && layer.outline.width > 0 && !colorAllowed(layer.outline.color, cfg.text.outlinePalette, cfg.text.allowCustomColor)) {
          problems.push({ code: 'color-not-allowed', layerId: id, detail: 'outline' });
        }
        if (!(typeof layer.sizeMm === 'number') || layer.sizeMm < cfg.text.minSizeMm - 1e-6 || layer.sizeMm > cfg.text.maxSizeMm + 1e-6) {
          problems.push({ code: 'size-out-of-range', layerId: id, detail: `${layer.sizeMm} mm` });
        }
        break;
      }
      case 'image': {
        if (!cfg.allow.image) { problems.push({ code: 'kind-not-allowed', layerId: id, detail: 'image' }); break; }
        if (layer.maskShape && !isShapeKind(layer.maskShape)) problems.push({ code: 'bad-answer', layerId: id, detail: 'Unknown image mask shape' });
        images += 1;
        if (images > cfg.image.maxCount) problems.push({ code: 'too-many-images', layerId: id, detail: `> ${cfg.image.maxCount}` });
        const info = ctx.asset?.(layer.assetId);
        if (ctx.asset && !info) { problems.push({ code: 'asset-unknown', layerId: id, detail: layer.assetId }); break; }
        // A small picture is never a problem here: the designer shows the dpi
        // and a "slab kvalitet" badge, the merchant sees it on the order, and
        // the sale goes through — `low-resolution` stays in the code union only
        // so older widgets' wording still resolves.
        break;
      }
      case 'artwork': {
        const info = ctx.asset?.(layer.assetId);
        if (ctx.asset && !info) { problems.push({ code: 'asset-unknown', layerId: id, detail: layer.assetId }); break; }
        const libraryId = info?.libraryId ?? layer.libraryId;
        const pack = info?.pack ?? (libraryId ? ctx.libraryPacks?.get(libraryId) : undefined) ?? layer.libraryId;
        const emoji = Boolean(pack && isEmojiLibrary(pack));
        if (emoji ? !cfg.allow.emoji : !cfg.allow.artwork) { problems.push({ code: 'kind-not-allowed', layerId: id, detail: emoji ? 'emoji' : 'artwork' }); break; }
        if (allowedLibraries && (!libraryId || !allowedLibraries.has(libraryId))) {
          problems.push({ code: 'library-not-allowed', layerId: id, detail: libraryId ?? 'no library' });
        }
        if (cfg.artwork.allowRecolor) {
          const colours = [
            ...(layer.singleColor ? [layer.singleColor] : []),
            ...(layer.colorize ? [layer.colorize] : []),
            ...(layer.colorMap?.map((m) => m.color) ?? []),
          ];
          if (colours.some((col) => !normalizeHex(col.hex))) problems.push({ code: 'color-not-allowed', layerId: id, detail: 'artwork colour' });
        }
        break;
      }
      case 'qr': {
        if (!cfg.allow.qr) problems.push({ code: 'kind-not-allowed', layerId: id, detail: 'qr' });
        // Unlike every other layer a QR is never merely clipped: a code missing
        // a corner, or its quiet zone, is a code that will not scan.
        if (overhang) {
          problems.push({ code: 'outside-zone', layerId: id, detail: 'Keep the complete QR and its white margin inside the print zone' });
        }
        try {
          validateDesignerQr(layer.url);
          const minSide = designerQrMinMm(layer.url);
          const square = Math.abs(box.w - box.h) <= 0.01;
          if (!square || Math.min(box.w, box.h) < minSide) throw new Error(`QR needs a square of at least ${minSide} mm`);
          // Mirrored or translucent modules defeat every scanner we can test.
          if (layer.flipX || layer.flipY || (layer.opacity ?? 1) !== 1) throw new Error('QR must stay opaque and unmirrored');
        } catch (error) {
          problems.push({ code: 'bad-answer', layerId: id, detail: error instanceof Error ? error.message : 'Invalid QR' });
        }
        break;
      }
      case 'shape': {
        if (!cfg.allow.shape) { problems.push({ code: 'kind-not-allowed', layerId: id, detail: 'shape' }); break; }
        if (!isShapeKind(layer.shape) || (cfg.shape.kinds.length && !cfg.shape.kinds.includes(layer.shape))) {
          problems.push({ code: 'shape-not-allowed', layerId: id, detail: String(layer.shape) });
        }
        if (layer.fill && !colorAllowed(layer.fill, cfg.shape.palette, cfg.text.allowCustomColor)) problems.push({ code: 'color-not-allowed', layerId: id, detail: 'fill' });
        if (layer.stroke && !colorAllowed(layer.stroke.color, cfg.shape.palette, cfg.text.allowCustomColor)) problems.push({ code: 'color-not-allowed', layerId: id, detail: 'stroke' });
        if (!layer.fill && !(layer.stroke && layer.stroke.widthMm > 0)) problems.push({ code: 'bad-answer', layerId: id, detail: 'shape has neither fill nor stroke' });
        break;
      }
      default:
        problems.push({ code: 'kind-not-allowed', layerId: id, detail: String((layer as { kind?: unknown }).kind) });
    }
  }
  return problems;
}

/** Repeated tiles one zone may carry in total, across every patterned layer. */
const MAX_TILES_PER_ZONE = 800;

/** A layer's own name, and a template field's shopper-facing label. */
export const LAYER_NAME_MAX = 80;
/** A text layer's content, and the authored original a template remembers. */
export const LAYER_TEXT_MAX = 2000;
export const TEMPLATE_LABEL_MAX = LAYER_NAME_MAX;
export const TEMPLATE_ORIGINAL_MAX = LAYER_TEXT_MAX;
/** A URL parameter name: what links duplicate fields to one another. */
export const TEMPLATE_KEY_PATTERN = /^[a-z0-9_-]{1,40}$/;

/**
 * What a Studio-authored "Selling Option" must look like, and whether the
 * shopper has done what it asked.
 *
 * `mustReplace` is the one that blocks an order: the layer still holds the
 * stock photo or the words "Vaše ime", which is a parcel nobody wants.
 */
function templateProblems(layer: DesignLayer): DesignProblem[] {
  const problems: DesignProblem[] = [];
  const id = layer.id;
  if (layer.interactive !== undefined && typeof layer.interactive !== 'boolean') {
    problems.push({ code: 'bad-answer', layerId: id, detail: 'interactive must be true or false' });
  }
  if (layer.permanent !== undefined && typeof layer.permanent !== 'boolean') {
    problems.push({ code: 'bad-answer', layerId: id, detail: 'permanent must be true or false' });
  }
  const template = layer.template;
  if (!template) return problems;
  if (typeof template.label !== 'string' || !template.label.trim() || template.label.length > TEMPLATE_LABEL_MAX) {
    problems.push({ code: 'bad-answer', layerId: id, detail: `Template label is required and limited to ${TEMPLATE_LABEL_MAX} characters` });
  }
  if (template.key !== undefined && (typeof template.key !== 'string' || !TEMPLATE_KEY_PATTERN.test(template.key))) {
    problems.push({ code: 'bad-answer', layerId: id, detail: 'Template key may hold up to 40 lowercase letters, digits, - or _' });
  }
  if (template.original !== undefined && (typeof template.original !== 'string' || template.original.length > TEMPLATE_ORIGINAL_MAX)) {
    problems.push({ code: 'bad-answer', layerId: id, detail: `Template original is limited to ${TEMPLATE_ORIGINAL_MAX} characters` });
  }
  if (!template.mustReplace || template.original === undefined) return problems;
  const untouched = layer.kind === 'text'
    ? layer.text === template.original
    : (layer.kind === 'image' || layer.kind === 'artwork') && layer.assetId === template.original;
  if (untouched) {
    problems.push({ code: 'template-unreplaced', layerId: id, detail: `Replace the placeholder "${template.label}" with your own content` });
  }
  return problems;
}

/**
 * The two sticker faults only a renderer can see, for the callers that have
 * one. Without `ctx.stickerContour` this is silent — the cut pipeline in
 * `@pz/scene-render` remains the authority.
 */
function stickerShapeProblems(answer: DesignAreaAnswer, ctx: DesignValidationContext): DesignProblem[] {
  const contour = ctx.stickerContour?.(answer);
  if (!contour) return [];
  const problems: DesignProblem[] = [];
  if (contour.touchesEdge) {
    problems.push({ code: 'sticker-edge', detail: 'The white border reaches the edge of the print area — move or shrink the artwork' });
  }
  if (contour.islands > STICKER_LIMITS.maxBridgedIslands) {
    problems.push({
      code: 'sticker-islands',
      detail: `The artwork falls into ${contour.islands + 1} separate pieces; a cutter can bridge at most ${STICKER_LIMITS.maxBridgedIslands + 1}`,
    });
  }
  return problems;
}

/** Built-in pack ids are `pack:<name>`; the emoji pack is the one `allow.emoji` governs. */
export const BUILTIN_PACK_PREFIX = 'pack:';
export const EMOJI_PACK_ID = 'pack:emoji';
export function isEmojiLibrary(libraryId: string): boolean {
  return libraryId === EMOJI_PACK_ID || /emoji/i.test(libraryId);
}

// ── Normalisation (raw JSON → typed answer) ──────────────────────────────────

function colorOf(value: unknown): Color | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const hex = normalizeHex((value as { hex?: unknown }).hex);
  if (!hex) return undefined;
  const alpha = (value as { alpha?: unknown }).alpha;
  return typeof alpha === 'number' && alpha >= 0 && alpha < 1 ? { hex, alpha } : { hex };
}

function finite(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function round(value: number, places = 3): number {
  const f = 10 ** places;
  return Math.round(value * f) / f;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

function boxOf(value: unknown): DesignBox | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as Record<string, unknown>;
  const x = finite(v.x), y = finite(v.y), w = finite(v.w), h = finite(v.h);
  if (x === undefined || y === undefined || w === undefined || h === undefined) return undefined;
  // Rotation is folded into 0–360 so two answers that differ only by a full
  // turn compare equal — the designer's undo stack depends on that.
  const rotation = finite(v.rotation);
  return { x: round(x), y: round(y), w: round(w), h: round(h), ...(rotation ? { rotation: round(((rotation % 360) + 360) % 360, 2) } : {}) };
}

/**
 * The Studio-authored "Selling Options" of one layer, as they survive the wire.
 *
 * Anything malformed is dropped rather than rejected: a template field is
 * authoring metadata, and losing a label must never cost the shopper the design
 * they built. `designAreaProblems` still judges what is left.
 */
function templateFieldOf(value: unknown): DesignTemplateField | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const label = typeof raw.label === 'string' ? raw.label.trim().slice(0, TEMPLATE_LABEL_MAX) : '';
  if (!label) return undefined;
  const key = typeof raw.key === 'string' && TEMPLATE_KEY_PATTERN.test(raw.key) ? raw.key : undefined;
  const original = typeof raw.original === 'string' ? raw.original.slice(0, TEMPLATE_ORIGINAL_MAX) : undefined;
  return {
    label,
    ...(key ? { key } : {}),
    ...(raw.editOnCard === true ? { editOnCard: true } : {}),
    ...(raw.mustReplace === true ? { mustReplace: true } : {}),
    ...(original !== undefined ? { original } : {}),
  };
}

/**
 * Parses whatever arrived on the wire into a typed answer, dropping unknown
 * keys and impossible values. `undefined` when it is not an answer at all;
 * validation (`designAreaProblems`) then judges what is left.
 */
export function normalizeDesignAnswer(raw: unknown): DesignAreaAnswer | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const v = raw as { v?: unknown; layers?: unknown; sticker?: unknown; background?: unknown };
  if (v.v !== DESIGN_ANSWER_VERSION || !Array.isArray(v.layers)) return undefined;
  if (v.sticker !== undefined && !validStickerSettings(v.sticker)) return undefined;
  if (v.background !== undefined && !colorOf(v.background)) return undefined;
  const layers: DesignLayer[] = [];
  for (const item of v.layers.slice(0, 200)) {
    if (!item || typeof item !== 'object') continue;
    const l = item as Record<string, unknown>;
    const id = typeof l.id === 'string' ? l.id.slice(0, 40) : '';
    const box = boxOf(l.box);
    if (!id || !box) continue;
    // Opacity is the ONE transparency field, 0–1; the UI shows it as 1–100.
    const opacity = finite(l.opacity);
    // A malformed pattern is a structural error, not a stray key: it would
    // silently print one motif where the shopper asked for a filled zone.
    let pattern: DesignerPattern | undefined;
    if (l.pattern !== undefined) { try { pattern = parseDesignerPattern(l.pattern); } catch { return undefined; } }
    if (l.visible !== undefined && typeof l.visible !== 'boolean') return undefined;
    if (l.orientation !== undefined && !['horizontal', 'vertical'].includes(String(l.orientation))) return undefined;
    if (l.fit !== undefined && !['cover', 'contain'].includes(String(l.fit))) return undefined;
    const template = templateFieldOf(l.template);
    const base = {
      id,
      box,
      ...(typeof l.name === 'string' && l.name.trim() ? { name: l.name.trim().slice(0, LAYER_NAME_MAX) } : {}),
      ...(l.visible === false ? { visible: false } : {}),
      ...(pattern ? { pattern } : {}),
      ...(typeof l.groupId === 'string' && l.groupId ? { groupId: l.groupId.slice(0, 40) } : {}),
      ...(opacity !== undefined && opacity >= 0 && opacity < 1 ? { opacity: round(opacity, 2) } : {}),
      ...(l.flipX === true ? { flipX: true } : {}),
      ...(l.flipY === true ? { flipY: true } : {}),
      ...(l.locked === true ? { locked: true } : {}),
      // Only the non-default is stored: interactive layers are the norm, so
      // `interactive` appears exactly when the author switched it off.
      ...(template ? { template } : {}),
      ...(l.permanent === true ? { permanent: true } : {}),
      ...(l.interactive === false ? { interactive: false } : {}),
    };
    switch (l.kind) {
      case 'text': {
        const color = colorOf(l.color);
        const sizeMm = finite(l.sizeMm);
        if (typeof l.text !== 'string' || typeof l.fontId !== 'string' || !color || sizeMm === undefined) continue;
        const outlineColor = l.outline && typeof l.outline === 'object' ? colorOf((l.outline as { color?: unknown }).color) : undefined;
        const outlineWidth = l.outline && typeof l.outline === 'object' ? finite((l.outline as { width?: unknown }).width) : undefined;
        const shadow = l.shadow && typeof l.shadow === 'object' ? (l.shadow as Record<string, unknown>) : undefined;
        const shadowColor = shadow ? colorOf(shadow.color) : undefined;
        const align = l.align === 'left' || l.align === 'right' || l.align === 'center' ? l.align : undefined;
        // Both new text fields are stored by their NON-default, the way
        // `interactive` and `permanent` are: an absent `boxMode` is the box that
        // hugs its text (every answer written before fixed boxes existed), and an
        // absent `anchorV` is the middle. An unknown value is dropped, not fatal
        // — a stray anchor must not cost a shopper the design they built.
        // Vertical text is never a fixed box, and a fixed box never bends.
        const boxMode = l.boxMode === 'fixed' && l.orientation !== 'vertical' ? ('fixed' as const) : undefined;
        const anchorV = l.anchorV === 'top' || l.anchorV === 'bottom' ? l.anchorV : undefined;
        const bend = boxMode || finite(l.bend) === undefined || Math.abs(finite(l.bend)!) < 1
          ? undefined
          : Math.round(Math.max(-100, Math.min(100, finite(l.bend)!)));
        const layer: DesignTextLayer = {
          ...base,
          kind: 'text',
          text: l.text.replace(/\r\n?/g, '\n').slice(0, TEMPLATE_ORIGINAL_MAX),
          ...(l.orientation === 'vertical' ? { orientation: 'vertical' as const } : {}),
          fontId: l.fontId,
          sizeMm: round(sizeMm),
          color,
          ...(align ? { align } : {}),
          ...(boxMode ? { boxMode } : {}),
          ...(anchorV ? { anchorV } : {}),
          ...(finite(l.lineSpacing) !== undefined ? { lineSpacing: round(Math.min(3, Math.max(0.5, finite(l.lineSpacing)!)), 2) } : {}),
          ...(finite(l.tracking) !== undefined ? { tracking: Math.round(Math.min(1000, Math.max(-500, finite(l.tracking)!))) } : {}),
          ...(l.uppercase === true ? { uppercase: true } : {}),
          ...(outlineColor && outlineWidth !== undefined && outlineWidth > 0
            ? { outline: { color: outlineColor, width: round(Math.min(0.5, outlineWidth), 3) } }
            : {}),
          ...(shadow && shadowColor
            ? {
              shadow: {
                color: shadowColor,
                offset: round(Math.min(100, Math.max(0, finite(shadow.offset) ?? 0)), 1),
                angle: round(finite(shadow.angle) ?? 45, 1),
                blur: round(Math.min(100, Math.max(0, finite(shadow.blur) ?? 0)), 1),
              },
            }
            : {}),
          ...(bend !== undefined ? { bend } : {}),
          ...(finite(l.textWidthMm) !== undefined && finite(l.textWidthMm)! > 0 ? { textWidthMm: round(finite(l.textWidthMm)!) } : {}),
          ...(typeof l.styleId === 'string' && l.styleId ? { styleId: l.styleId.slice(0, 40) } : {}),
        };
        layers.push(layer);
        break;
      }
      case 'image': {
        if (typeof l.assetId !== 'string' || !l.assetId) continue;
        let erase: import('./photo-erase.js').PhotoEraseStroke[] | undefined;
        try { if (l.erase !== undefined) erase = parsePhotoErase(l.erase); } catch { return undefined; }
        if (l.maskShape !== undefined && !isShapeKind(String(l.maskShape))) return undefined;
        const crop = l.crop && typeof l.crop === 'object' ? (l.crop as Record<string, unknown>) : undefined;
        const cx = crop ? finite(crop.cx) : undefined, cy = crop ? finite(crop.cy) : undefined, zoom = crop ? finite(crop.zoom) : undefined;
        const effects = Array.isArray(l.effects)
          ? (l.effects as unknown[]).flatMap((e): DesignImageEffect[] => {
            if (!e || typeof e !== 'object') return [];
            const kind = (e as { kind?: unknown }).kind;
            if (kind === 'whiteTransparent') {
              return [(e as { connected?: unknown }).connected === true ? { kind, connected: true } : { kind }];
            }
            if (kind === 'look') {
              const look = designerLook((e as { id?: unknown }).id);
              return look ? [{ kind, id: look.id }] : [];
            }
            if (kind === 'removeBackground' || kind === 'grayscale' || kind === 'sepia') return [{ kind }];
            if (kind === 'brightness' || kind === 'contrast') {
              const amount = finite((e as { amount?: unknown }).amount);
              return amount === undefined ? [] : [{ kind, amount: round(Math.max(-1, Math.min(1, amount)), 2) }];
            }
            return [];
          })
          : [];
        const source = l.sourcePx && typeof l.sourcePx === 'object' ? (l.sourcePx as Record<string, unknown>) : undefined;
        const sw = source ? finite(source.w) : undefined, sh = source ? finite(source.h) : undefined;
        // The crop window: centre as a 0–1 fraction of the source, zoom 1–8.
        // A partial crop is no crop — all three parts or none, so the renderer
        // never has to guess what half a frame meant.
        const framed = cx !== undefined && cy !== undefined && zoom !== undefined && zoom > 0;
        const cropRotation = crop ? finite(crop.rotation) : undefined;
        const framing: ImageCrop | undefined = framed
          ? {
            cx: round(clamp(cx, 0, 1)),
            cy: round(clamp(cy, 0, 1)),
            zoom: round(clamp(zoom, 1, 8)),
            ...(cropRotation ? { rotation: round(cropRotation, 1) } : {}),
          }
          : undefined;
        const layer: DesignImageLayer = {
          ...base,
          kind: 'image',
          assetId: l.assetId,
          ...(l.maskShape ? { maskShape: l.maskShape as ShapeKind } : {}),
          ...(erase?.length ? { erase } : {}),
          ...(l.fit === 'contain' ? { fit: 'contain' as const } : {}),
          ...(framing ? { crop: framing } : {}),
          ...(effects.length ? { effects } : {}),
          ...(sw && sh && sw > 0 && sh > 0 ? { sourcePx: { w: Math.round(sw), h: Math.round(sh) } } : {}),
        };
        layers.push(layer);
        break;
      }
      case 'artwork': {
        if (typeof l.assetId !== 'string' || !l.assetId) continue;
        const colorMap = Array.isArray(l.colorMap)
          ? (l.colorMap as unknown[]).flatMap((m) => {
            if (!m || typeof m !== 'object') return [];
            const from = normalizeHex((m as { from?: unknown }).from);
            const color = colorOf((m as { color?: unknown }).color);
            return from && color ? [{ from, color }] : [];
          }).slice(0, 16)
          : [];
        const single = colorOf(l.singleColor), tint = colorOf(l.colorize);
        const layer: DesignArtworkLayer = {
          ...base,
          kind: 'artwork',
          assetId: l.assetId,
          ...(typeof l.libraryId === 'string' && l.libraryId ? { libraryId: l.libraryId.slice(0, 80) } : {}),
          ...(l.vector === true ? { vector: true } : {}),
          ...(colorMap.length ? { colorMap } : {}),
          ...(single ? { singleColor: single } : {}),
          ...(tint ? { colorize: tint } : {}),
        };
        layers.push(layer);
        break;
      }
      case 'qr': {
        // A QR whose URL we cannot validate is not a layer to drop quietly:
        // the shopper would ship a code that goes nowhere. Refuse the answer.
        try { layers.push({ ...base, kind: 'qr', url: validateDesignerQr(l.url) }); } catch { return undefined; }
        break;
      }
      case 'shape': {
        if (!isShapeKind(l.shape)) continue;
        const fill = colorOf(l.fill);
        const stroke = l.stroke && typeof l.stroke === 'object' ? (l.stroke as Record<string, unknown>) : undefined;
        const strokeColor = stroke ? colorOf(stroke.color) : undefined;
        const strokeWidth = stroke ? finite(stroke.widthMm) : undefined;
        const layer: DesignShapeLayer = {
          ...base,
          kind: 'shape',
          shape: l.shape,
          ...(fill ? { fill } : {}),
          ...(strokeColor && strokeWidth !== undefined && strokeWidth > 0 ? { stroke: { color: strokeColor, widthMm: round(Math.min(20, strokeWidth), 2) } } : {}),
        };
        layers.push(layer);
        break;
      }
      default:
        continue;
    }
  }
  const background = colorOf(v.background);
  return {
    v: DESIGN_ANSWER_VERSION,
    layers,
    ...(background ? { background } : {}),
    // Rebuilt field by field rather than copied, so an unknown sticker key
    // cannot ride along into an answer the cut pipeline will read.
    ...(validStickerSettings(v.sticker) ? { sticker: { borderMm: v.sticker.borderMm, bridgeMm: v.sticker.bridgeMm, ...(v.sticker.imageBorder === undefined ? {} : { imageBorder: v.sticker.imageBorder }), ...(v.sticker.outline === undefined ? {} : { outline: v.sticker.outline }) } } : {}),
  };
}

export function isDesignAreaAnswer(value: unknown): value is DesignAreaAnswer {
  return Boolean(value) && typeof value === 'object'
    && (value as { v?: unknown }).v === DESIGN_ANSWER_VERSION
    && Array.isArray((value as { layers?: unknown }).layers);
}

/** True when there is nothing to print: no backdrop and nothing left showing. */
export function isEmptyDesign(answer: DesignAreaAnswer | null | undefined): boolean {
  if (!answer) return true;
  return !answer.background && answer.layers.every((layer) => layer.visible === false);
}

// ── Price ────────────────────────────────────────────────────────────────────

export interface DesignPriceOptions {
  /**
   * Layers the ZONE came with — the author's own composition, which the widget
   * seeds the shopper's answer from.
   *
   * They are part of the product, not something the shopper added, so the
   * per-layer surcharges skip them. Identity is by layer id and nothing else: a
   * shopper who deletes an authored line and types their own has added a layer
   * and pays for it, while one who retypes the authored line in place has not.
   * `authoredLayerIds(zone)` is where the list comes from.
   */
  authoredLayerIds?: Iterable<string>;
}

/** The ids of a zone's authored starting composition — the free ones. */
export function authoredLayerIds(zone: Pick<DesignAreaElement, 'value'>): string[] {
  return (zone.value?.layers ?? []).map((layer) => layer.id);
}

/** Surcharge for a design, in the shop currency. Zero for an empty one. */
export function designAreaPrice(
  config: DesignAreaConfig,
  answer: DesignAreaAnswer | null | undefined,
  options: DesignPriceOptions = {},
): number {
  if (isEmptyDesign(answer)) return 0;
  const cfg = resolveDesignAreaConfig(config);
  const authored = options.authoredLayerIds ? new Set(options.authoredLayerIds) : EMPTY_IDS;
  // The base is a charge for personalising at all, so it stands even when every
  // layer on the canvas is one the author put there.
  let total = cfg.pricing.base;
  for (const layer of answer!.layers) {
    if (layer.visible === false) continue;
    if (authored.has(layer.id)) continue;
    if (layer.kind === 'text') total += cfg.pricing.perText;
    else if (layer.kind === 'image') total += cfg.pricing.perImage;
    else if (layer.kind === 'artwork') total += cfg.pricing.perArtwork;
    else if (layer.kind === 'shape') total += cfg.pricing.perShape;
    else if (layer.kind === 'qr') total += cfg.pricing.perShape;
  }
  if (cfg.pricing.max !== undefined) total = Math.min(total, cfg.pricing.max);
  return Math.round(total * 100) / 100;
}

// ── Summaries ────────────────────────────────────────────────────────────────

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10, mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${n} ${few}`;
  return `${n} ${many}`;
}

/**
 * The design in one line for the cart, the order and the mail: the texts
 * verbatim (they are what gets printed), the rest counted.
 */
export function describeDesign(answer: DesignAreaAnswer | null | undefined): string {
  if (isEmptyDesign(answer)) return '';
  // Only what prints is described; a layer switched off is not in the parcel.
  const shown = answer!.layers.filter((layer) => layer.visible !== false);
  const texts = shown.filter((l): l is DesignTextLayer => l.kind === 'text').map((l) => `„${l.text.replace(/\n/g, ' / ')}“`);
  const images = shown.filter((l) => l.kind === 'image').length;
  const artwork = shown.filter((l) => l.kind === 'artwork').length;
  const shapes = shown.filter((l) => l.kind === 'shape').length;
  const qr = shown.filter((l) => l.kind === 'qr').length;
  const parts: string[] = [];
  if (answer!.background) parts.push(`Pozadina: ${answer!.background.hex}`);
  if (qr) parts.push(`${qr} QR`);
  if (texts.length) parts.push(`Tekst: ${texts.join(', ')}`);
  if (images) parts.push(plural(images, 'slika', 'slike', 'slika'));
  if (artwork) parts.push(plural(artwork, 'ukras', 'ukrasa', 'ukrasa'));
  if (shapes) parts.push(plural(shapes, 'oblik', 'oblika', 'oblika'));
  return parts.join(' · ');
}

/** Every asset id a design refers to (uploads and artwork). */
export function designAssetIds(answer: DesignAreaAnswer | null | undefined): string[] {
  if (!answer) return [];
  const ids = new Set<string>();
  for (const layer of answer.layers) {
    if (layer.kind === 'image' || layer.kind === 'artwork') ids.add(layer.assetId);
  }
  return [...ids];
}

/** Every font id a design uses. */
export function designFontIds(answer: DesignAreaAnswer | null | undefined): string[] {
  if (!answer) return [];
  return [...new Set(answer.layers.filter((l): l is DesignTextLayer => l.kind === 'text').map((l) => l.fontId))];
}

// ── Ids ──────────────────────────────────────────────────────────────────────

let counter = 0;

/** A short id unique within one answer (`t1k9-3f`), never a UUID — it is typed into element ids. */
export function newDesignLayerId(): string {
  counter = (counter + 1) % 1296;
  const time = Date.now().toString(36).slice(-4);
  const rand = Math.floor(Math.random() * 1296).toString(36).padStart(2, '0');
  return `${time}${rand}${counter.toString(36).padStart(2, '0')}`;
}

/** A fresh text layer centred in the zone, sized to a fifth of its height. */
export function newTextLayer(config: DesignAreaConfig, fontId: string, text?: string): DesignTextLayer {
  const cfg = resolveDesignAreaConfig(config);
  const style = cfg.text.styles[0] ?? DESIGN_TEXT_STYLES[0]!;
  const sizeMm = Math.max(cfg.text.minSizeMm, Math.min(cfg.text.maxSizeMm, cfg.sizeMm.h * 0.2));
  const value = text ?? cfg.text.defaultText;
  const w = Math.min(cfg.sizeMm.w * 0.9, Math.max(sizeMm, [...value].length * sizeMm * 0.6));
  return {
    id: newDesignLayerId(),
    kind: 'text',
    box: { x: cfg.sizeMm.w / 2, y: cfg.sizeMm.h / 2, w: round(w), h: round(sizeMm * 1.2) },
    text: value,
    fontId,
    sizeMm: round(sizeMm),
    color: style.color,
    align: 'center',
    ...(style.outline ? { outline: style.outline } : {}),
    ...(style.shadow ? { shadow: style.shadow } : {}),
    styleId: style.id,
  };
}

/** A fresh picture/artwork box: fits 60 % of the zone, keeps the source aspect. */
export function fittedBox(config: DesignAreaConfig, aspect: number, fraction = 0.6): DesignBox {
  const cfg = resolveDesignAreaConfig(config);
  const maxW = cfg.sizeMm.w * fraction, maxH = cfg.sizeMm.h * fraction;
  const ratio = aspect > 0 && Number.isFinite(aspect) ? aspect : 1;
  let w = maxW, h = w / ratio;
  if (h > maxH) { h = maxH; w = h * ratio; }
  return { x: cfg.sizeMm.w / 2, y: cfg.sizeMm.h / 2, w: round(w), h: round(h) };
}
